import type { ComponentType } from "react";
import { cn } from "@slumbr/ui";
import type { AppInfo } from "@/stores/open-apps-store";
import { GAME_REGISTRY } from "@/apps/games/registry";

// Games are NOT listed in the main apps launcher — only the "Games" app is.
// But opening a specific game promotes it to its own app: its own icon/tab in
// the app bar, distinct from the Games store. These helpers synthesise that
// per-game AppInfo on demand (cached so the icon component identity is stable).

const iconCache = new Map<string, ComponentType<{ className?: string }>>();
function emojiIcon(id: string, glyph: string): ComponentType<{ className?: string }> {
  const cached = iconCache.get(id);
  if (cached) return cached;
  const Comp = ({ className }: { className?: string }) => (
    <span className={cn("flex items-center justify-center text-[17px] leading-none", className)}>{glyph}</span>
  );
  Comp.displayName = `GameIcon(${id})`;
  iconCache.set(id, Comp);
  return Comp;
}

const appCache = new Map<string, AppInfo>();

/** AppInfo for a game-play route (`/games/{gameId}/...`), or null for the
 *  Games store root (`/games`) and non-game routes. */
export function gameAppForPath(pathname: string): AppInfo | null {
  const parts = pathname.split("/").filter(Boolean); // e.g. ["games","poker","menu"]
  if (parts[0] !== "games" || !parts[1]) return null;
  const entry = GAME_REGISTRY.find((g) => g.id === parts[1]);
  if (!entry) return null;
  let info = appCache.get(entry.id);
  if (!info) {
    info = {
      id: `game:${entry.id}`,
      name: entry.name,
      icon: emojiIcon(entry.id, entry.icon),
      color: entry.coverGradient,
      route: `/games/${entry.id}`,
    };
    appCache.set(entry.id, info);
  }
  return info;
}
